/**
 * AI Chat Service
 * 
 * Service for managing AI chat conversations and messages.
 * Handles communication with the AI Chat API endpoints.
 * 
 * Dependencies: HttpClient, AuthService
 * Used by: QuickChatComponent
 * 
 * @version PCTMv1.0.0-1-FEAT
 */

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, tap, catchError, throwError } from 'rxjs';
import { environment } from 'environments/environment';

// AI Chat Types
export interface MessageContext {
    type: 'course' | 'module' | 'lesson' | 'subject';
    id: string;
}

export interface ContextOption {
    id: string;
    name: string;
    description?: string;
    type: string;
}

export interface AIChatMessage {
    _id: string;
    conversationId: string;
    messageId: string;
    role: 'user' | 'assistant';
    content: string;
    status: string;
    messageOrder: number;
    metadata?: {
        openaiCreatedAt?: number;
        runId?: string;
        tokensUsed?: number;
    };
    processedAt?: string;
    createdAt: string;
    updatedAt: string;
}

export interface AIChatConversation {
    _id: string;
    userId: string;
    threadId: string;
    title: string;
    status: 'active' | 'archived' | 'deleted';
    messageCount: number;
    lastMessageAt: string;
    metadata?: {
        openaiCreatedAt?: number;
    };
    createdAt: string;
    updatedAt: string;
    messages: AIChatMessage[];
    id: string;
}

export interface AIChatConversationResponse {
    message: string;
    data: AIChatConversation;
}

export interface SendMessageRequest {
    content: string;
    context?: MessageContext;
}

export interface SendMessageResponse {
    message: string;
    data: {
        response: string;
        conversationId: string;
        messageId: string;
        isNewConversation: boolean;
        metadata: {
            tokensUsed: number;
            processingTime: number;
        };
    };
}

@Injectable({
    providedIn: 'root'
})
export class AIChatService {
    private readonly _apiUrl = `${environment.apiUrl}/ai/chat`;
    
    // Internal state
    private _activeConversation$ = new BehaviorSubject<AIChatConversation | null>(null);
    private _loading$ = new BehaviorSubject<boolean>(false);
    private _sendingMessage$ = new BehaviorSubject<boolean>(false);

    // Public observables
    get activeConversation$(): Observable<AIChatConversation | null> {
        return this._activeConversation$.asObservable();
    }

    get loading$(): Observable<boolean> {
        return this._loading$.asObservable();
    }

    get sendingMessage$(): Observable<boolean> {
        return this._sendingMessage$.asObservable();
    }

    constructor(private _httpClient: HttpClient) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get the active conversation
     */
    getActiveConversation(): Observable<AIChatConversationResponse> {
        this._loading$.next(true);
        
        return this._httpClient.get<AIChatConversationResponse>(`${this._apiUrl}/conversation`).pipe(
            tap((response) => {
                this._activeConversation$.next(response.data);
                this._loading$.next(false);
            }),
            catchError((error) => {
                this._loading$.next(false);
                console.error('Error fetching active conversation:', error);
                // Re-throw the error so it can be handled by the calling service
                return throwError(() => error);
            })
        );
    }

    /**
     * Send a message to the AI assistant
     */
    sendMessage(content: string, context?: MessageContext): Observable<SendMessageResponse> {
        this._sendingMessage$.next(true);
        
        const request: SendMessageRequest = { content };
        if (context) {
            request.context = context;
        }
        
        return this._httpClient.post<SendMessageResponse>(`${this._apiUrl}/message`, request).pipe(
            tap((response) => {
                // Refresh the active conversation after sending a message
                this.getActiveConversation().subscribe();
                this._sendingMessage$.next(false);
            }),
            catchError((error) => {
                this._sendingMessage$.next(false);
                console.error('Error sending message:', error);
                return throwError(() => error);
            })
        );
    }

    /**
     * Delete a conversation by ID
     */
    deleteConversation(conversationId: string): Observable<any> {
        this._loading$.next(true);
        
        return this._httpClient.delete(`${this._apiUrl}/conversations/${conversationId}`).pipe(
            tap(() => {
                // Clear the active conversation from local state after successful deletion
                this._activeConversation$.next(null);
                this._loading$.next(false);
            }),
            catchError((error) => {
                this._loading$.next(false);
                console.error('Error deleting conversation:', error);
                return throwError(() => error);
            })
        );
    }

    /**
     * Clear the active conversation from local state
     */
    clearActiveConversation(): void {
        this._activeConversation$.next(null);
    }

    /**
     * Get current active conversation from local state
     */
    getCurrentConversation(): AIChatConversation | null {
        return this._activeConversation$.value;
    }

    /**
     * Get available context options for the specified type
     * This method is a placeholder and should be implemented to fetch actual data
     * from the backend or other services
     */
    getAvailableContextOptions(type: string, studentId: string): Observable<ContextOption[]> {
        // This is a placeholder implementation
        // In a real scenario, this would fetch data from appropriate endpoints
        // For now, return an empty array
        return new Observable(observer => {
            observer.next([]);
            observer.complete();
        });
    }
}
